package com.example.borshi.web.nutritionist.models

/**
 * Patient data model for nutritionist management
 * Part of the Nutritionist Module - can be extracted as independent module
 */
data class Patient(
    val patientId: String = "",
    val userId: String = "",
    val name: String = "",
    val email: String = "",
    val phone: String = "",
    val profileImage: String = "",

    // Health Information
    val age: Int = 0,
    val gender: String = "",
    val height: Double = 0.0, // in cm
    val weight: Double = 0.0, // in kg
    val bmi: Double = 0.0,

    // Medical Information
    val healthConditions: List<String> = emptyList(),
    val allergies: List<String> = emptyList(),
    val medications: List<String> = emptyList(),
    val dietaryRestrictions: List<String> = emptyList(),

    // Goals & Preferences
    val healthGoals: List<String> = emptyList(),
    val activityLevel: String = "moderate",
    val foodPreferences: List<String> = emptyList(),

    // Plan Information
    val currentPlanId: String? = null,
    val planStartDate: Long = 0,
    val adherenceScore: Int = 0, // 0-100

    // Monitoring
    val lastCheckIn: Long = 0,
    val nextAppointment: Long = 0,
    val alertsCount: Int = 0,

    // Metadata
    val assignedNutritionistId: String = "",
    val status: PatientStatus = PatientStatus.ACTIVE,
    val createdAt: Long = 0,
    val updatedAt: Long = 0
)

enum class PatientStatus {
    ACTIVE,
    INACTIVE,
    COMPLETED,
    ON_HOLD
}

/**
 * Patient progress tracking
 */
data class PatientProgress(
    val progressId: String = "",
    val patientId: String = "",
    val recordDate: Long = 0,

    // Measurements
    val weight: Double = 0.0,
    val bmi: Double = 0.0,
    val bodyFat: Double? = null,
    val muscleMass: Double? = null,

    // Progress metrics
    val adherenceScore: Int = 0,
    val mealsCompleted: Int = 0,
    val mealsPlanned: Int = 0,

    // Feedback
    val notes: String = "",
    val mood: String = "",
    val energyLevel: Int = 0, // 1-5

    // Nutritionist notes
    val nutritionistNotes: String = "",
    val adjustmentsNeeded: Boolean = false
)

/**
 * Alert for patient monitoring
 */
data class PatientAlert(
    val alertId: String = "",
    val patientId: String = "",
    val type: AlertType = AlertType.INFO,
    val severity: AlertSeverity = AlertSeverity.LOW,
    val title: String = "",
    val message: String = "",
    val actionRequired: Boolean = false,
    val isRead: Boolean = false,
    val createdAt: Long = 0
)

enum class AlertType {
    ADHERENCE,
    HEALTH_METRIC,
    APPOINTMENT,
    PLAN_EXPIRY,
    INFO
}

enum class AlertSeverity {
    LOW,
    MEDIUM,
    HIGH,
    CRITICAL
}

